/**
 * Shared utilities, types, and constants for SkillDebugHUD and its sub-modules.
 */

import { safeStringify } from '@shared/safeStringify';
import type { SkillDebugEvent, SkillDebugEventType } from '../../lib/skill-debug-events';
import { isPlainObject } from '../../lib/skill-stream-schemas';
import type { ToolEndPayload, ToolStartPayload } from '@shared/schemas/skill-stream-events';

// Constants

export const ALL_TYPES: SkillDebugEventType[] = [
  'delta',
  'tool_start',
  'tool_end',
  'complete',
  'error',
  'draft_update',
  'timing',
  'connection_open',
  'connection_close',
  'connection_error',
  'cache_invalidate',
  'app_home_sse',
];

// Pill styling per event type

export interface PillStyle {
  bg: string;
  text: string;
  label: string;
}

export const PILL_STYLES: Record<SkillDebugEventType, PillStyle> = {
  delta: { bg: 'bg-bg-tertiary', text: 'text-content-tertiary', label: 'Response' },
  tool_start: { bg: 'bg-bg-orange-light', text: 'text-content-warning', label: 'Calling' },
  tool_end: { bg: 'bg-bg-green-light', text: 'text-content-success', label: 'Done' },
  complete: { bg: 'bg-bg-blue-light', text: 'text-content-action', label: 'Complete' },
  error: { bg: 'bg-bg-red-light', text: 'text-content-danger', label: 'Error' },
  draft_update: { bg: 'bg-bg-pink-light', text: 'text-content-action', label: 'Updated' },
  timing: { bg: 'bg-bg-blue-light', text: 'text-content-action', label: 'Timing' },
  connection_open: { bg: 'bg-bg-green-light', text: 'text-content-success', label: 'Connected' },
  connection_close: { bg: 'bg-bg-tertiary', text: 'text-content-tertiary', label: 'Closed' },
  connection_error: { bg: 'bg-bg-red-light', text: 'text-content-danger', label: 'Disconnected' },
  cache_invalidate: { bg: 'bg-bg-pink-light', text: 'text-content-action', label: 'Cache' },
  app_home_sse: { bg: 'bg-bg-yellow-light', text: 'text-content-warning', label: 'Broadcast' },
};

/** Narrative descriptions for known tools */
export const TOOL_NARRATIVES: Record<string, string> = {
  get_artifact_detail: 'Read the artifact\u2019s current content before making changes',
  compose_form_field: 'Wrote new content for a field based on the conversation',
  search_artifacts: 'Searched for artifacts matching a query',
  get_blueprint_config: 'Loaded the blueprint to understand available fields and actions',
  execute_action: 'Executed an action on the artifact',
  list_blueprints: 'Listed available blueprints',
  list_action_definitions: 'Listed available actions for a blueprint',
  get_execution_history: 'Loaded previous action history for this artifact',
  run_analytics: 'Ran an analytics query across artifacts',
  complete_task: 'Marked the current task as complete',
};

// Helpers

/** Format a relative time offset like +0.0s, +1.4s, +12.3s */
export function formatRelativeTime(ts: number, baseTs: number): string {
  const delta = (ts - baseTs) / 1000;
  if (delta < 0) return '+0.0s';
  if (delta < 10) return `+${delta.toFixed(1)}s`;
  if (delta < 100) return `+${delta.toFixed(1)}s`;
  return `+${Math.round(delta)}s`;
}

export function totalChars(events: SkillDebugEvent[]): number {
  let n = 0;
  for (const ev of events) {
    const s = typeof ev.data === 'string' ? ev.data : JSON.stringify(ev.data);
    n += s?.length ?? 0;
  }
  return n;
}

/** Assemble text from a group of delta events */
export function assembleDeltaText(events: SkillDebugEvent[]): string {
  const parts: string[] = [];
  for (const ev of events) {
    if (ev.type === 'delta' && ev.data) {
      parts.push(ev.data.text);
    }
  }
  return parts.join('');
}

/** Truncate a value to a preview string */
export function truncatePreview(value: unknown, maxLen: number): string {
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  if (!s) return 'null';
  return s.length > maxLen ? s.slice(0, maxLen) + '...' : s;
}

/** Recursively search an object for the first string value that looks like HTML */
export function findHtmlString(data: unknown): string | null {
  if (typeof data === 'string') {
    if (/<body[\s>]|<p[\s>]|<div[\s>]|<table[\s>]|<html[\s>]/i.test(data)) {
      return data;
    }
    return null;
  }
  if (Array.isArray(data)) {
    for (const item of data) {
      const found = findHtmlString(item);
      if (found) return found;
    }
    return null;
  }
  if (data && typeof data === 'object') {
    for (const val of Object.values(data)) {
      const found = findHtmlString(val);
      if (found) return found;
    }
  }
  return null;
}

/** Extract tool name from a tool_start/tool_end event's payload */
export function getToolName(data: ToolStartPayload | ToolEndPayload | null | undefined): string | null {
  return data?.name ?? null;
}

/** Check if a tool_end payload has error status */
export function isToolError(data: ToolEndPayload | null | undefined): boolean {
  return data?.status === 'error';
}

/** Format a duration in ms to human-readable */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 10000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 1000)}s`;
}

export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Convert snake_case tool/field names to Title Case */
export function humanize(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Convert camelCase field names to readable form */
export function humanizeCamel(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

/** Strip HTML tags and decode entities for text-level diffing */
export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Summarize a tool result as key-value pairs instead of raw JSON */
export function summarizeResult(toolName: string, result: unknown): { key: string; value: string }[] {
  if (!isPlainObject(result)) return [];
  const r = result;

  if (toolName === 'get_artifact_detail' && isPlainObject(r.artifact)) {
    const a = r.artifact;
    const items: { key: string; value: string }[] = [];
    if (a.kind) items.push({ key: 'Type', value: humanize(safeStringify(a.kind)) });
    if (a.uuid) items.push({ key: 'ID', value: safeStringify(a.uuid).slice(0, 12) + '\u2026' });
    // Summarize actions
    const actions = a.actions;
    if (Array.isArray(actions)) {
      const labels = actions
        .map((act: unknown) => {
          if (isPlainObject(act) && typeof act.label === 'string' && act.label.length > 0) return act.label;
          return humanize(safeStringify(isPlainObject(act) ? (act.id ?? '') : ''));
        })
        .join(', ');
      items.push({ key: 'Actions', value: labels });
    }
    // Show HTML field sizes only — skip raw data dumps
    const data = a.data;
    if (isPlainObject(data)) {
      for (const [k, v] of Object.entries(data)) {
        if (typeof v === 'string' && v.length > 100) {
          const isHtml = /<[a-z][\s\S]*>/i.test(v);
          items.push({ key: humanizeCamel(k), value: `${v.length.toLocaleString()} chars${isHtml ? ' (HTML)' : ''}` });
        }
      }
    }
    return items;
  }

  if (toolName === 'compose_form_field') {
    const items: { key: string; value: string }[] = [];
    if (r.field) items.push({ key: 'Field', value: humanizeCamel(safeStringify(r.field)) });
    const val = r.value ?? r.content;
    if (typeof val === 'string') {
      const isHtml = /<[a-z][\s\S]*>/i.test(val);
      items.push({ key: 'Content', value: `${val.length.toLocaleString()} chars${isHtml ? ' \u2014 see Preview tab' : ''}` });
    }
    return items;
  }

  // Generic: show top-level keys with humanized names, skip large/noisy values
  const items: { key: string; value: string }[] = [];
  for (const [k, v] of Object.entries(r)) {
    if (typeof v === 'string' && v.length > 200) {
      items.push({ key: humanizeCamel(k), value: `${v.length.toLocaleString()} chars` });
    } else if (v != null && typeof v !== 'object') {
      items.push({ key: humanizeCamel(k), value: safeStringify(v) });
    }
  }
  return items.slice(0, 5);
}
